/**
 * createMarketingTransport — the ONE transport every marketing site passes (AISEO-GA4-001).
 *
 * Today each of the nine `MarketingWebsiteApp.jsx` files hands `createMarketingApp` a single
 * transport:
 *
 *     transport: createHttpAnalyticsTransport({ endpoint: '/api/v1/analytics/ingest' })
 *
 * GA4 must be ADDED to that, never substituted for it — the first-party ingest is the copy we own,
 * survives ad-blockers, and is the only one queryable from our own stack. So the seam needs a
 * transport that fans out, and this is it. The GA4 measurement ID needs no argument — it resolves
 * from the current hostname via the SSOT map, which is what lets the three vanity brands served by
 * purchase-web report into their OWN properties:
 *
 *     transport: createMarketingTransport({ onError: reportAnalyticsFailure })
 *
 * ## Why a composite rather than editing nine files to pass an array
 *
 * An array would push the fan-out policy into nine call sites, so adding a third destination later
 * would be a nine-file change again — and the nine would drift, which is how five of them ended up
 * with subtly different marketing wiring before. One factory means the next destination is added
 * once, here.
 *
 * ## Isolation between destinations is the load-bearing property
 *
 * A transport that throws must not stop its siblings. GA4's `gtag` is third-party code injected at
 * runtime; if it throws (blocked, half-initialised, or a Google-side change) a naive
 * `transports.forEach(t => t.track(e))` loses the FIRST-PARTY event too — the ad-blocked visitors
 * would silently vanish from our own analytics, which is precisely backwards. Each destination is
 * therefore isolated, and a failure is reported rather than swallowed.
 *
 * @module @puffin-core/marketing-shell/analytics/createMarketingTransport
 */

import createHttpAnalyticsTransport from './createHttpAnalyticsTransport.js';
import createGa4Transport from './createGa4Transport.js';
import { resolveGa4MeasurementId } from './ga4Measurement.js';
import requireOnError from './requireOnError.js';

/** Every marketing site posts its first-party events to the same app-relative path. */
export const DEFAULT_INGEST_ENDPOINT = '/api/v1/analytics/ingest';

/**
 * Fan one envelope out to many transports, isolating each.
 *
 * Exported because the isolation semantics are worth testing directly, and because a consumer with
 * a bespoke destination set should compose rather than re-implement.
 *
 * @param {Array<{track: Function}>} transports
 * @param {(error: Error) => void} onError  REQUIRED: receives each isolated destination's throw.
 * @returns {{track: (envelope: Object) => void}}
 * @throws {TypeError} when `onError` is not a function
 */
export function composeTransports(transports, onError) {
  const list = (Array.isArray(transports) ? transports : []).filter(
    (t) => t && typeof t.track === 'function',
  );
  requireOnError('composeTransports', onError);

  return {
    track(envelope) {
      for (const transport of list) {
        try {
          transport.track(envelope);
        } catch (err) {
          // Isolated on purpose: one destination's failure must not cost the others their event.
          onError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    },
  };
}

/**
 * @typedef {Object} MarketingTransportConfig
 * @property {string} [ingestEndpoint]     First-party ingest path. Defaults to the fleet path.
 * @property {string} [ga4MeasurementId]   GA4 `G-XXXXXXXXXX`. Omitted ⇒ resolved from the current
 *   hostname via the SSOT map; an unmapped host (localhost, preview, tunnel) yields no GA4
 *   transport, which is what keeps dev traffic out of production properties.
 * @property {(error: Error) => void} onError  REQUIRED: handed to every destination it builds and
 *   to the composer, so a failure anywhere in the fan-out is reported, never swallowed.
 * @property {Object} [windowRef]          Injectable window (tests / SSR).
 * @property {string} [hostname]           Override the host used for GA4 resolution (tests / SSR).
 */

/**
 * Build the composed marketing transport.
 *
 * **An unresolved measurement ID is a supported steady state, not a misconfiguration.** It is the
 * normal case on localhost, preview hosts and tunnels — and keeping dev traffic OUT of production
 * properties matters more than instrumenting dev, because once aggregated it is invisible. The site
 * is still fully instrumented via first-party ingest either way, so GA4 is attached only when an ID
 * resolves and its absence is silent: throwing here would take a marketing site down over
 * telemetry.
 *
 * @param {MarketingTransportConfig} config
 * @returns {{track: (envelope: Object) => void}}
 * @throws {TypeError} when `config` is not an object, or `onError` is not a function
 */
export default function createMarketingTransport(config) {
  if (typeof config !== 'object' || config === null) {
    throw new TypeError('createMarketingTransport: config must be an object');
  }
  const {
    ingestEndpoint = DEFAULT_INGEST_ENDPOINT,
    ga4MeasurementId = resolveGa4MeasurementId(config.hostname),
    onError,
    windowRef,
  } = config;
  // Refused here, before any destination is built, so the refusal names THIS factory.
  requireOnError('createMarketingTransport', onError);

  const transports = [createHttpAnalyticsTransport({ endpoint: ingestEndpoint, onError })];

  if (typeof ga4MeasurementId === 'string' && ga4MeasurementId.trim()) {
    transports.push(
      createGa4Transport({ measurementId: ga4MeasurementId.trim(), onError, windowRef }),
    );
  }

  return composeTransports(transports, onError);
}
